import { ReactElement, ReactNode, useLayoutEffect, useRef } from "react";
import { MenuListProps } from "react-select";
import { List, RowComponentProps, useListCallbackRef } from "react-window";

import { useRtl } from "@ryde/redux";
import { IDropdownItem } from "@ryde/shared";

import { FILTER_CONST } from "./filter-multiselect-models";
import { menuListInitialOffsetResolve } from "./filter-multiselect-utils";

interface IFilterMenuListProps extends MenuListProps {
  filteredOptions: MenuListProps["options"];
}

interface IFilterMenuRowProps {
  menuChildren: ReactNode[];
  filteredOptions: MenuListProps["options"];
}

const FilterMenuRow = ({
  index,
  style,
  menuChildren,
  filteredOptions,
}: RowComponentProps<IFilterMenuRowProps>): ReactElement => {
  return (
    <div
      style={style}
      className="truncate-text"
      title={String((filteredOptions[index] as IDropdownItem)?.label)}
    >
      {menuChildren[index]}
    </div>
  );
};

export const FilterMenuList = ({
  children,
  maxHeight,
  getValue,
  filteredOptions,
  selectProps,
}: IFilterMenuListProps): ReactElement | null => {
  const { direction } = useRtl();
  const [listApi, setListApi] = useListCallbackRef();
  const menuChildren = children ? (children as ReactNode[]) : [];
  const values = getValue();
  const initialOffset = menuListInitialOffsetResolve({
    filteredOptions,
    values,
    maxHeight,
  });

  const appliedOffsetKeyRef = useRef<string | undefined>(undefined);
  const offsetKey = `${selectProps.inputValue}|${filteredOptions.length}`;

  useLayoutEffect(() => {
    const listElement = listApi?.element;

    if (
      !listElement ||
      menuChildren.length === 0 ||
      appliedOffsetKeyRef.current === offsetKey
    ) {
      return;
    }

    appliedOffsetKeyRef.current = offsetKey;
    // Assigned even when it is 0: a fresh search has to be able to scroll back to the top.
    listElement.scrollTop = initialOffset;
  }, [initialOffset, listApi, menuChildren.length, offsetKey]);

  if (!children) {
    return null;
  }

  return (
    <List
      listRef={setListApi}
      rowComponent={FilterMenuRow}
      rowCount={menuChildren.length}
      rowHeight={FILTER_CONST.LIST_ITEM_HEIGHT}
      rowProps={{ menuChildren, filteredOptions }}
      dir={direction}
      overscanCount={FILTER_CONST.OVERSCAN_COUNT}
      style={{
        width: FILTER_CONST.LIST_ITEM_WIDTH,
        height: maxHeight,
      }}
    />
  );
};
